<script setup>
import { nextTick, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  VIDEO_CAPTURE_CONSTRAINTS,
  getSupportedVideoCaptureMimeTypes,
} from './mediaCaptureHelper';

const props = defineProps({
  selectedVideoInputDeviceId: {
    type: String,
    default: '',
  },
  selectedAudioInputDeviceId: {
    type: String,
    default: '',
  },
  preferredVideoMimeType: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['capture-video', 'video-error']);

const { t } = useI18n();

const dialogRef = ref(null);
const videoRef = ref(null);
const streamRef = ref(null);
const mediaRecorderRef = ref(null);
const recordedChunksRef = ref([]);
const shouldSaveRecording = ref(false);
const recordingTimerRef = ref(null);
const isOpen = ref(false);
const isStartingCamera = ref(false);
const isRecordingVideo = ref(false);
const recordingDurationInSeconds = ref(0);

const formatRecordingDuration = totalSeconds => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;
};

const stopTracks = () => {
  streamRef.value?.getTracks().forEach(track => track.stop());
  streamRef.value = null;
  if (videoRef.value) {
    videoRef.value.srcObject = null;
  }
};

const stopRecorder = () => {
  if (mediaRecorderRef.value && mediaRecorderRef.value.state !== 'inactive') {
    mediaRecorderRef.value.stop();
  }
};

const startRecordingTimer = () => {
  recordingDurationInSeconds.value = 0;
  recordingTimerRef.value = window.setInterval(() => {
    recordingDurationInSeconds.value += 1;
  }, 1000);
};

const stopRecordingTimer = () => {
  if (recordingTimerRef.value) {
    window.clearInterval(recordingTimerRef.value);
    recordingTimerRef.value = null;
  }
};

const getVideoErrorKey = error => {
  switch (error?.name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'permission';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'device_not_found';
    case 'NotReadableError':
    case 'TrackStartError':
      return 'device_unavailable';
    default:
      return 'generic';
  }
};

const getSupportedMimeType = () =>
  props.preferredVideoMimeType || getSupportedVideoCaptureMimeTypes()[0] || '';

const startPreview = async () => {
  if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
    emit('video-error', { key: 'generic' });
    close();
    return;
  }

  isStartingCamera.value = true;
  stopTracks();

  try {
    const videoConstraints = props.selectedVideoInputDeviceId
      ? {
          deviceId: {
            exact: props.selectedVideoInputDeviceId,
          },
          ...VIDEO_CAPTURE_CONSTRAINTS,
        }
      : {
          facingMode: 'environment',
          ...VIDEO_CAPTURE_CONSTRAINTS,
        };

    const audioConstraints = props.selectedAudioInputDeviceId
      ? {
          deviceId: {
            exact: props.selectedAudioInputDeviceId,
          },
        }
      : true;

    const stream = await navigator.mediaDevices.getUserMedia({
      video: videoConstraints,
      audio: audioConstraints,
    });

    streamRef.value = stream;
    if (videoRef.value) {
      videoRef.value.srcObject = stream;
      await videoRef.value.play();
    }
  } catch (error) {
    emit('video-error', {
      key: getVideoErrorKey(error),
      errorName: error?.name || '',
    });
    close();
  } finally {
    isStartingCamera.value = false;
  }
};

const open = async () => {
  isOpen.value = true;
  dialogRef.value?.open();
  await nextTick();
  await startPreview();
};

const close = () => {
  shouldSaveRecording.value = false;
  stopRecordingTimer();
  stopRecorder();
  dialogRef.value?.close();
};

const handleDialogClose = () => {
  stopTracks();
  stopRecordingTimer();
  recordedChunksRef.value = [];
  mediaRecorderRef.value = null;
  shouldSaveRecording.value = false;
  isRecordingVideo.value = false;
  recordingDurationInSeconds.value = 0;
  isOpen.value = false;
};

const finalizeRecording = () => {
  const mimeType =
    mediaRecorderRef.value?.mimeType || getSupportedMimeType() || 'video/webm';
  const blob = new Blob(recordedChunksRef.value, { type: mimeType });
  if (!blob.size) {
    emit('video-error', { key: 'generic' });
    return;
  }

  const extension = mimeType.includes('webm') ? 'webm' : 'mp4';
  const file = new File([blob], `video-${Date.now()}.${extension}`, {
    type: mimeType,
  });

  emit('capture-video', {
    file,
    name: file.name,
    type: file.type,
    size: file.size,
  });
  close();
};

const toggleRecording = () => {
  if (!streamRef.value) {
    emit('video-error', { key: 'generic' });
    return;
  }

  if (isRecordingVideo.value) {
    shouldSaveRecording.value = true;
    stopRecorder();
    return;
  }

  try {
    const mimeType = getSupportedMimeType();
    recordedChunksRef.value = [];
    mediaRecorderRef.value = mimeType
      ? new MediaRecorder(streamRef.value, { mimeType })
      : new MediaRecorder(streamRef.value);

    mediaRecorderRef.value.ondataavailable = event => {
      if (event.data?.size) {
        recordedChunksRef.value.push(event.data);
      }
    };
    mediaRecorderRef.value.onstop = () => {
      isRecordingVideo.value = false;
      stopRecordingTimer();
      if (shouldSaveRecording.value) {
        finalizeRecording();
      }
    };
    mediaRecorderRef.value.start();
    shouldSaveRecording.value = false;
    startRecordingTimer();
    isRecordingVideo.value = true;
  } catch (error) {
    emit('video-error', {
      key: getVideoErrorKey(error),
      errorName: error?.name || '',
    });
  }
};

watch(
  () => [props.selectedVideoInputDeviceId, props.selectedAudioInputDeviceId],
  async (nextDevices, previousDevices) => {
    if (!isOpen.value || isRecordingVideo.value) {
      return;
    }

    if (
      nextDevices[0] === previousDevices?.[0] &&
      nextDevices[1] === previousDevices?.[1]
    ) {
      return;
    }

    await startPreview();
  }
);

onUnmounted(() => {
  stopRecordingTimer();
  stopRecorder();
  stopTracks();
});

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :title="t('CONVERSATION.REPLYBOX.VIDEO_CAPTURE_TITLE')"
    :description="t('CONVERSATION.REPLYBOX.VIDEO_CAPTURE_DESCRIPTION')"
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close="handleDialogClose"
  >
    <div class="flex flex-col gap-4">
      <div class="video-preview-wrap" :class="{ 'is-loading': isStartingCamera }">
        <video ref="videoRef" autoplay playsinline muted class="video-preview" />
        <div v-if="isStartingCamera" class="video-preview__overlay">
          {{ t('CONVERSATION.REPLYBOX.VIDEO_CAPTURE_LOADING') }}
        </div>
        <div
          v-else-if="isRecordingVideo"
          class="video-preview__recording-indicator"
        >
          {{
            t('CONVERSATION.REPLYBOX.VIDEO_CAPTURE_RECORDING', {
              duration: formatRecordingDuration(recordingDurationInSeconds),
            })
          }}
        </div>
      </div>
    </div>
    <template #footer>
      <div class="flex items-center justify-between gap-3">
        <Button
          type="button"
          variant="faded"
          color="slate"
          :label="t('DIALOG.BUTTONS.CANCEL')"
          class="w-full"
          @click="close"
        />
        <Button
          type="button"
          :color="isRecordingVideo ? 'ruby' : 'blue'"
          :label="
            isRecordingVideo
              ? t('CONVERSATION.REPLYBOX.VIDEO_CAPTURE_STOP')
              : t('CONVERSATION.REPLYBOX.VIDEO_CAPTURE_START')
          "
          class="w-full"
          :disabled="isStartingCamera"
          @click="toggleRecording"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped lang="scss">
.video-preview-wrap {
  @apply relative overflow-hidden rounded-xl border border-n-container bg-n-alpha-2 min-h-72;
}

.video-preview {
  @apply block w-full h-72 object-cover bg-n-slate-2;
}

.video-preview__overlay,
.video-preview__recording-indicator {
  @apply absolute inset-x-0 flex items-center justify-center text-sm font-medium text-white backdrop-blur-sm;
}

.video-preview__overlay {
  @apply inset-y-0 bg-n-alpha-3;
}

.video-preview__recording-indicator {
  @apply top-3 mx-auto w-fit rounded-full bg-n-ruby-9/90 px-3 py-1;
}
</style>
